import React from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link, type WithRouterProps} from 'react-router';
import styled from 'styled-components';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {getObjectAcceptURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {useAcceptance} from '../api';
import {isUserAcceptancePending} from '../utils';

import type {GenericAcceptableDocument} from '../types';

const StyledBarDIV = styled.div`
  padding: 10px 20px;
  text-align: center;
  transition: all 0.5s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border-top: 1px solid ${colors.borderGray};
  background-color: #fff9e5;
`;
type Props<T> = {
  document?: GenericAcceptableDocument;
  openedVersion: T | undefined;
  location: WithRouterProps['location'];
};

export function PendingAcceptanceInfoBar<
  T extends {
    id: number;
    approved_at: string | null;
  }
>({document, location, openedVersion}: Props<T>) {
  const acceptance = useAcceptance(
    document == null
      ? skipToken
      : {
          object_pk: document.id,
          content_type: document.content_type,
        }
  ).data?.results?.[0];
  const userAcceptanceIsPending = isUserAcceptancePending(document, acceptance);

  if (acceptance == null) {
    return null;
  }

  let message;
  const name = acceptance.requester
    ? acceptance.requester.name
    : gt('Deleted user');

  if (openedVersion == null && userAcceptanceIsPending) {
    message = gt('%(name)s has requested your approval', {
      name,
    });
  }

  if (
    openedVersion != null &&
    openedVersion.approved_at == null &&
    openedVersion.id === acceptance.object_id
  ) {
    message = userAcceptanceIsPending
      ? gt('%(name)s requested your approval for this version', {
          name,
        })
      : gt('%(name)s requested approval for this version', {
          name,
        });
  }

  if (message == null) {
    return null;
  }

  return (
    <StyledBarDIV>
      <div className="d-flex align-items-center justify-content-center lead">
        <div className="text-truncate flex-grow-1">
          <i className="fa fa-fw fa-thumbs-up mr-2 small" />
          {message}{' '}
          <ElapsedTime
            className="d-none d-md-inline"
            tooltipPlacement="top"
            ContainerTag="span"
            timestamp={acceptance.created_at}
          />
        </div>
        <Link
          to={getObjectAcceptURL(
            document,
            acceptance.object_id,
            location.search
          )}
          className="btn btn-sm btn-warning flex-shrink-0 ml-auto"
        >
          {gt('Check it out!')}
        </Link>
      </div>
    </StyledBarDIV>
  );
}
